import * as React from 'react';
import { useEffect, useRef } from 'react';

import { GenerationStatus } from '@showbie-socrative/socrative-utils';

import { useGetActivityInsights } from '~teacher/hook/activity/insights/useGetActivityInsights';

import { InsightsCompleted } from './InsightsComplete';
import { InsightsFailed } from './InsightsFailed';
import { InsightsInsufficientData } from './InsightsInsufficientData';
import { InsightsLoading } from './InsightsLoading';
import { InsightsNone } from './InsightsNone';
import { InsightsPending } from './InsightsPending';

interface InsightsProps {
  showInsights?: boolean;
  activityInstanceId: number;
  questionIdToNumberMap: Map<string, number>;
  participantUuidToNameMap: Map<string, string>;
  averageScoreRate?: number | null;
}

export function Insights({
  showInsights = false,
  activityInstanceId,
  questionIdToNumberMap,
  participantUuidToNameMap,
  averageScoreRate,
}: InsightsProps) {
  const activityInsightsQuery = useGetActivityInsights({
    activityInstanceId,
    retry: false,
  });
  const generationStatus = activityInsightsQuery.data?.generationStatus;

  const wasPending = useTrackWasPending(generationStatus);

  if (activityInsightsQuery.isInitialLoading) {
    return <InsightsLoading />;
  } else if (!activityInsightsQuery.data) {
    return (
      <InsightsNone
        activityInstanceId={activityInstanceId}
        averageScoreRate={averageScoreRate}
      />
    );
  } else {
    const activityInsights = activityInsightsQuery.data;
    switch (activityInsights.generationStatus) {
      case GenerationStatus.Pending:
        return <InsightsPending activityInstanceId={activityInstanceId} />;

      case GenerationStatus.Completed:
        return (
          <InsightsCompleted
            activityInstanceId={activityInstanceId}
            insights={activityInsights}
            questionIdToNumberMap={questionIdToNumberMap}
            participantUuidToNameMap={participantUuidToNameMap}
            averageScoreRate={averageScoreRate}
            defaultOpen={wasPending || showInsights}
          />
        );

      case GenerationStatus.Failed:
        return (
          <InsightsFailed
            activityInstanceId={activityInstanceId}
            averageScoreRate={averageScoreRate}
          />
        );

      case GenerationStatus.InsufficientData:
        return (
          <InsightsInsufficientData
            activityInstanceId={activityInstanceId}
            averageScoreRate={averageScoreRate}
          />
        );

      default:
        return (
          <InsightsNone
            activityInstanceId={activityInstanceId}
            averageScoreRate={averageScoreRate}
          />
        );
    }
  }
}

/**
 * To track if the generation was pending during the component lifecycle
 */
function useTrackWasPending(generationStatus: GenerationStatus) {
  const wasPendingRef = useRef(false);
  // Track if generation was pending during component lifecycle
  useEffect(() => {
    if (generationStatus === GenerationStatus.Pending) {
      wasPendingRef.current = true;
    }
  }, [generationStatus]);
  return wasPendingRef.current;
}
